import { useEffect, useState } from 'react'
import { ferry, clock, type Me, type Sailing, type Traveller } from '../lib/ferry'
import { t } from '../lib/i18n'
import SeatMap from './SeatMap'
import Icon from './Icon'

interface Passenger { name: string; kind: 'adult' | 'child' | 'senior'; needsPriority: boolean; note: string }

const blank = (): Passenger => ({ name: '', kind: 'adult', needsPriority: false, note: '' })

const fromSaved = (s: Traveller): Passenger => ({
  name: s.name,
  kind: (['adult', 'child', 'senior'].includes(s.kind) ? s.kind : 'adult') as Passenger['kind'],
  needsPriority: s.needs_priority === 1,
  note: s.note ?? '',
})

/**
 * Buy a crossing.
 *
 * The party is the unit throughout — not the individual — because that is how
 * people actually travel and how they expect to be treated at the gate. One
 * booking, one pass, one scan.
 */
export default function Book({ sailing, me, onDone, onCancel }: {
  sailing: Sailing
  me: Me | null
  onDone: (bookingId: string, totalCents: number, reference: string) => void
  onCancel: () => void
}) {
  const [party, setParty] = useState<Passenger[]>([blank()])
  const [extras, setExtras] = useState({ bicycle: 0, pet: 0, luggage: 0 })
  const [vehicleId, setVehicleId] = useState('')
  const [seats, setSeats] = useState<string[]>([])
  const [saved, setSaved] = useState<Traveller[]>([])
  const [remember, setRemember] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const resident = me?.isResident ?? false
  const canChooseSeat = me?.canChooseSeat ?? false
  const vehicles = (me?.vehicles ?? []).filter((v) => v.status === 'verified')
  const carriesVehicles = sailing.vessel_kind !== 'passenger'

  /*
   * The people this account has travelled with before.
   *
   * This is the whole reason the address book exists: a family books the same
   * four names every crossing, and retyping them was the most annoying thing
   * about this screen. The FIRST saved traveller is pre-filled into row one,
   * because the overwhelmingly common case is the account holder booking for
   * themselves — and an empty name field they have filled in twenty times is a
   * small insult.
   */
  useEffect(() => {
    ferry.travellers()
      .then((d) => {
        setSaved(d.travellers)
        if (d.travellers.length) setParty([fromSaved(d.travellers[0]!)])
      })
      .catch(() => { /* an address book that will not load is not worth an error here */ })
  }, [])

  const set = (i: number, patch: Partial<Passenger>) =>
    setParty(party.map((p, n) => (n === i ? { ...p, ...patch } : p)))

  const alreadyOnTrip = (name: string) => party.some((p) => p.name.trim().toLowerCase() === name.toLowerCase())

  async function submit() {
    setBusy(true); setError('')
    try {
      const b = await ferry.book({
        sailingId: sailing.id,
        passengers: party.map((p) => ({
          name: p.name.trim(), kind: p.kind,
          needsPriority: p.needsPriority,
          priorityReason: p.note.trim() || undefined,
        })),
        extras: [
          ...Object.entries(extras).filter(([, n]) => n > 0).map(([kind, quantity]) => ({ kind, quantity })),
          ...(vehicleId ? [{ kind: 'vehicle', vehicleId }] : []),
        ],
        seatIds: canChooseSeat && seats.length === party.length ? seats : undefined,
        rememberTravellers: remember,
      })
      onDone(b.id, b.total_cents, b.reference)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const named = party.every((p) => p.name.trim())
  const unused = saved.filter((s) => !alreadyOnTrip(s.name))

  return (
    <section className="card">
      <button className="back with-icon" onClick={onCancel}><Icon name="back" size={16} />{t('sail.back')}</button>

      <h1>{clock(sailing.scheduled_departure)} · {sailing.vessel_name}</h1>
      <p className="muted">
        {sailing.route.split('-').map((s) => s[0]!.toUpperCase() + s.slice(1)).join(' → ')} · {sailing.seatsLeft} {t('sail.seatsLeft')}
      </p>

      {/*
       * Residency is stated here rather than only in the total, because the
       * difference between the two fares is large enough that seeing it applied
       * is the point. A resident charged the visitor rate who only finds out at
       * the total assumes the app is broken.
       */}
      <p className={`notice ${resident ? 'good' : ''}`}>
        <Icon name={resident ? 'check' : 'info'} size={16} />
        {resident ? t('book.residentFares') : t('book.visitorFares')}
      </p>

      <h2>{t('book.who')}</h2>

      {/* One tap adds somebody already known, which is the common case after
          the first trip. */}
      {unused.length > 0 && (
        <div className="saved-travellers">
          <p className="muted small">{t('book.savedHint')}</p>
          <div className="chips">
            {unused.map((s) => (
              <button key={s.id} type="button" className="chip"
                onClick={() => setParty([...party.filter((p) => p.name.trim()), fromSaved(s)])}>
                <Icon name="plus" size={14} />{s.name}
                {s.needs_priority ? <Icon name="accessible" size={13} /> : null}
              </button>
            ))}
          </div>
        </div>
      )}

      {party.map((p, i) => (
        <fieldset key={i} className="passenger">
          <legend>{t('book.passenger')} {i + 1}</legend>
          <label>
            <span>{t('book.name')}</span>
            <input value={p.name} onChange={(e) => set(i, { name: e.target.value })} placeholder={t('book.nameHint')} />
          </label>
          <label>
            <span>{t('book.fare')}</span>
            <select value={p.kind} onChange={(e) => set(i, { kind: e.target.value as Passenger['kind'] })}>
              <option value="adult">{t('book.adult')}</option>
              <option value="child">{t('book.child')}</option>
              <option value="senior">{t('book.senior')}</option>
            </select>
          </label>
          {/*
           * Priority is asked as a plain question, never inferred from the fare
           * class. Somebody 40 with a broken ankle needs it and somebody 60 who
           * runs marathons does not; a rule that guesses gets both wrong.
           */}
          <label className="check">
            <input type="checkbox" checked={p.needsPriority} onChange={(e) => set(i, { needsPriority: e.target.checked })} />
            <span>{t('book.priority')}</span>
          </label>
          {p.needsPriority && (
            <label>
              <span>{t('book.priorityNote')}</span>
              <input value={p.note} onChange={(e) => set(i, { note: e.target.value })} />
            </label>
          )}
          {party.length > 1 && (
            <button type="button" className="link" onClick={() => setParty(party.filter((_, n) => n !== i))}>
              {t('book.remove')}
            </button>
          )}
        </fieldset>
      ))}

      {party.length < 12 && (
        <button className="with-icon" onClick={() => setParty([...party, blank()])}>
          <Icon name="plus" size={16} />{t('book.addPassenger')}
        </button>
      )}
      {party.length >= 12 && <p className="muted small">{t('book.tooMany')}</p>}

      <label className="check remember">
        <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
        <span>
          {t('book.saveThem')}
          <em className="muted small">{t('book.savedNote')}</em>
        </span>
      </label>

      <h2>{t('book.bringing')}</h2>
      <div className="extras">
        {([['bicycle', 'bicycle'], ['pet', 'pet'], ['luggage', 'luggage']] as const).map(([kind, icon]) => (
          <label key={kind}>
            <span><Icon name={icon} size={16} />{t(`book.${kind}` as 'book.bicycle')}</span>
            <input type="number" min={0} max={6} value={extras[kind]}
              onChange={(e) => setExtras({ ...extras, [kind]: Math.max(0, Number(e.target.value) || 0) })} />
          </label>
        ))}
      </div>

      {/*
       * A vehicle is only offered when it can actually travel: verified
       * ownership, verified residency, and a vessel that carries cars. Offering
       * it and refusing at the ramp is the version that ruins a day.
       */}
      {carriesVehicles && vehicles.length > 0 && (
        <label>
          <span><Icon name="car" size={16} />{t('book.vehicle')}</span>
          <select value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>
            <option value="">{t('book.noVehicle')}</option>
            {vehicles.map((v) => <option key={v.id} value={v.id}>{v.plate}{v.make ? ` · ${v.make}` : ''}</option>)}
          </select>
        </label>
      )}
      {!carriesVehicles && <p className="muted small">{t('sail.peopleOnly')}</p>}

      <h2>{t('book.seats')}</h2>
      {canChooseSeat
        ? <SeatMap sailingId={sailing.id} need={party.length} chosen={seats} onChange={setSeats} />
        : <p className="muted small">{t('book.seatsAssigned')}</p>}

      {error && <p className="error">{error}</p>}

      <button className="primary with-icon" disabled={busy || !named} onClick={() => void submit()}>
        <Icon name="ticket" size={18} />{busy ? t('book.booking') : t('book.continue')}
      </button>
      <p className="muted small">{t('book.confirmFirst')}</p>
    </section>
  )
}
